import { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { RouterContext, goTo, useOnUnload } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler, List, ListItem, getKeyHandler, useConfirmationModal } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { getAdminMagicSecret } from '../../connector/auth';
import { TextButton, Note, useToast, Input } from 't-lib-ui/widgets';
import { chevronBackIcon, trashIcon, addIcon, chevronDownIcon, chevronForwardIcon, sparklesIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import AdminHeader from '../../components/admin/AdminHeader';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { useGetStuff, useSetStuff, getAutocomplete, getFeedSummaries } from '../../connector/api';
import { DiscoverConfig, DiscoverCollection } from '../../schema-copy/api-schema';
import { QueryStatus } from 't-lib-ui/connector';
import { nonNull } from 't-lib-ui/helpers';
import { debounce1 } from 't-lib-ui/helpers';
import { sortCollectionsByPriority } from './sortCollections';
import './AdminDiscoverPage.css';

interface FeedInfo {
  id: string;
  title: string;
  domain: string;
}

interface RenderFeedProps {
  feedInfo: FeedInfo;
  promoted: boolean;
  onTogglePromoted: (feedId: string) => void;
  onDelete: (feedId: string) => void;
}

const RenderFeed: React.FC<RenderFeedProps> = ({ feedInfo, promoted, onTogglePromoted, onDelete }) => {
  return (
    <ListItem focussable="normal" href={`/a/admin/catalog/${feedInfo.id}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500 }}>{feedInfo.title}</div>
          <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', marginTop: '4px' }}>
            {feedInfo.domain} • {feedInfo.id}
          </div>
        </div>
        <TextButton
          label={promoted ? 'Promoted' : 'Promote'}
          icon={sparklesIcon}
          onClick={() => onTogglePromoted(feedInfo.id)}
          focussable="flow"
          size="small"
          type={promoted ? 'major' : 'minor'}
        />
        <TextButton
          label="Delete"
          icon={trashIcon}
          onClick={() => onDelete(feedInfo.id)}
          focussable="flow"
          size="small"
          type="minor"
        />
      </div>
    </ListItem>
  );
};

interface CollectionEditorProps {
  collection: DiscoverCollection;
  isExpanded: boolean;
  onToggle: () => void;
  onChange: (updated: DiscoverCollection) => void;
  onDelete: () => void;
  /** The priority field was left: time to re-sort. */
  onPriorityCommit: () => void;
  feedInfoMap: Map<string, FeedInfo>;
  onSearchFeed: (query: string) => Promise<FeedInfo[]>;
}

const CollectionEditor: React.FC<CollectionEditorProps> = ({
  collection,
  isExpanded,
  onToggle,
  onChange,
  onDelete,
  onPriorityCommit,
  feedInfoMap,
  onSearchFeed
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FeedInfo[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const showConfirmDelete = useConfirmationModal({
    title: 'Delete Collection?',
    confirmContent: 'Delete',
    dismissContent: 'Cancel',
    children: `Are you sure you want to delete "${collection.title}"?`,
    onConfirm: onDelete
  });

  const debouncedSearch = useMemo(
    () => debounce1(1, async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        setSelectedIndex(-1);
        return;
      }

      setSearchLoading(true);
      try {
        const results = await onSearchFeed(query);
        setSearchResults(results);
        setShowSearchResults(true);
        setSelectedIndex(results.length == 1 ? 0 : -1);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400),
    [onSearchFeed]
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleAddFeed = useCallback((feedInfo: FeedInfo) => {
    if (collection.feedIds.some(f => f.feedId == feedInfo.id)) {
      return;
    }

    onChange({
      ...collection,
      feedIds: [
        ...collection.feedIds,
        {
          feedId: feedInfo.id,
          title: feedInfo.title,
          iconUrl: '', // iconUrl and siteUrl are populated from feed data at generation time
          siteUrl: ''
        }
      ]
    });

    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setSelectedIndex(-1);
  }, [collection, onChange]);

  const promotedCount = collection.feedIds.filter(f => f.promoted).length;

  const handleTogglePromoted = (feedId: string) => {
    onChange({
      ...collection,
      feedIds: collection.feedIds.map(f => f.feedId == feedId ? { ...f, promoted: !f.promoted || undefined } : f)
    });
  };

  const handleRemoveFeed = (feedId: string) => {
    onChange({
      ...collection,
      feedIds: collection.feedIds.filter(f => f.feedId != feedId)
    });
  };

  const handleInputKeyDown = useMemo(() => {
    const flowHandler = getKeyHandler({
      enterBehaviour: 'next',
      spaceBehaviour: 'ignore',
      sidewaysBehaviour: 'leftAtBeginning'
    });

    return (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (showSearchResults && searchResults.length > 0) {
        if (e.key == 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
          return;
        } else if (e.key == 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
          return;
        } else if (e.key == 'Enter') {
          e.preventDefault();
          if (searchResults.length == 1) {
            handleAddFeed(searchResults[0]);
          } else if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
            handleAddFeed(searchResults[selectedIndex]);
          }
          return;
        } else if (e.key == 'Escape') {
          setShowSearchResults(false);
          return;
        }
      }
      flowHandler(e as any);
    };
  }, [showSearchResults, searchResults, selectedIndex, handleAddFeed]);

  return (
    <div style={{
      border: '1px solid var(--gray-850)',
      borderRadius: '6px',
      marginBottom: '16px',
      overflow: 'hidden'
    }}>
      <div
        onClick={onToggle}
        style={{
          padding: '16px',
          background: isExpanded ? 'var(--gray-975)' : 'var(--surface-raised)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderBottom: isExpanded ? '1px solid var(--gray-850)' : 'none'
        }}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <TextButton
            icon={isExpanded ? chevronDownIcon : chevronForwardIcon}
            onClick={onToggle}
            focussable="flow"
            size="small"
            type="minor"
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 600, fontSize: '1rem' }}>{collection.title || '(Untitled)'}</span>
            {collection.topic?.trim() && collection.topic.trim() != collection.title && (
              <span style={{ fontSize: '0.85rem', color: 'var(--gray-400)' }}>“{collection.topic.trim()}”</span>
            )}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--gray-400)', marginTop: '4px' }}>
            {collection.feedIds.length} feeds{promotedCount ? ` (${promotedCount} promoted)` : ''} • {collection.id}
            {collection.priority != null && ` • priority ${collection.priority}`}
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <TextButton
            label="Delete"
            icon={trashIcon}
            onClick={showConfirmDelete}
            focussable="flow"
            size="small"
            type="minor"
          />
        </div>
      </div>

      {isExpanded && (
        <div style={{ padding: '20px', background: 'var(--surface-raised)' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>
              ID (read-only)
            </label>
            <div style={{
              padding: '8px 12px',
              background: 'var(--gray-975)',
              border: '1px solid var(--gray-850)',
              borderRadius: '4px',
              fontSize: '0.9rem',
              color: 'var(--gray-350)',
              fontFamily: 'monospace'
            }}>
              {collection.id}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>
              Title
            </label>
            <Input
              value={collection.title}
              onInput={(value) => onChange({ ...collection, title: value })}
              placeholder="Collection Title"
              layout="singleCol"
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>
              Topic
            </label>
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: '8px' }}>
              The name shown to users. Leave empty to use the title.
            </div>
            <Input
              value={collection.topic || ''}
              onInput={(value) => onChange({ ...collection, topic: value || undefined })}
              placeholder="e.g. Technology, Cooking, Local News"
              layout="singleCol"
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>
              Priority
            </label>
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: '8px' }}>
              Sort order: higher numbers are shown first; leave empty to sort last.
            </div>
            <Input
              type="number"
              value={collection.priority == null ? '' : String(collection.priority)}
              onInput={(value) => {
                const trimmed = value.trim();
                const n = Number(trimmed);
                onChange({ ...collection, priority: trimmed && Number.isFinite(n) ? n : undefined });
              }}
              onBlur={onPriorityCommit}
              placeholder="e.g. 10"
              layout="singleCol"
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '12px', fontSize: '0.9rem' }}>
              Feeds ({collection.feedIds.length})
            </label>

            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onBlur={() => {
                  setTimeout(() => {
                    setShowSearchResults(false);
                    setSelectedIndex(-1);
                  }, 200);
                }}
                placeholder="Search feeds to add..."
                className="flowFocussable noClickFocus"
                tabIndex={0}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--gray-800)',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
              />
              {searchLoading && (
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--gray-400)',
                  fontSize: '0.85rem'
                }}>
                  Loading...
                </div>
              )}
              {showSearchResults && searchResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'var(--surface-raised)',
                  border: '1px solid var(--gray-800)',
                  borderRadius: '4px',
                  marginTop: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}>
                  {searchResults.map((result, index) => {
                    const isAlreadyAdded = collection.feedIds.some(f => f.feedId == result.id);
                    const isSelected = index == selectedIndex;
                    return (
                      <div
                        key={result.id}
                        onClick={() => handleAddFeed(result)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: index < searchResults.length - 1 ? '1px solid var(--gray-950)' : 'none',
                          backgroundColor: isAlreadyAdded ? 'var(--gray-975)' : (isSelected ? 'var(--gray-925)' : 'var(--surface-raised)')
                        }}
                      >
                        <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                          {result.title}
                          {isAlreadyAdded && (
                            <span style={{ color: 'var(--success-color)', marginLeft: '8px', fontSize: '0.8rem' }}>✓ Added</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '2px' }}>
                          {result.domain}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <List>
              {collection.feedIds.length == 0 && (
                <div style={{ textAlign: 'center', color: 'var(--gray-500)', fontSize: '0.9rem' }}>
                  No feeds in this collection yet
                </div>
              )}
              {collection.feedIds.map((feed) => {
                const feedInfo = feedInfoMap.get(feed.feedId) || { id: feed.feedId, title: feed.title, domain: '' };
                return (
                  <RenderFeed
                    key={feed.feedId}
                    feedInfo={feedInfo}
                    promoted={!!feed.promoted}
                    onTogglePromoted={handleTogglePromoted}
                    onDelete={handleRemoveFeed}
                  />
                );
              })}
            </List>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDiscoverPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [, setSaving] = useState(false);
  const [feedInfoMap, setFeedInfoMap] = useState<Map<string, FeedInfo>>(new Map());
  const [showToast, toastProps] = useToast();

  const [collections, setCollections] = useState<DiscoverCollection[]>([]);
  const [originalCollections, setOriginalCollections] = useState<DiscoverCollection[]>([]);
  const [expandedCollectionIds, setExpandedCollectionIds] = useState<Set<string>>(new Set());

  const magic = getAdminMagicSecret() || adminSecret || '';

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminDiscoverPage', {
      'Escape': () => {
        goTo(routerCtx, '/a/admin/feeds');
        return true;
      },
    }, false, 0);

    return () => unregisterKeyHandler('adminDiscoverPage');
  }, [routerCtx]);

  // Load discover config
  const { data: discoverConfig, status: loadStatus } = useGetStuff<DiscoverConfig>('discover/config', magic);
  const setStuff = useSetStuff<DiscoverConfig>('discover/config', magic);

  // Initialize collections from loaded config and load feed info for all collection feeds
  useEffect(() => {
    if (!discoverConfig?.collections) return;
    // Older configs still carry removed fields; drop them so the next save cleans them up.
    const loaded = sortCollectionsByPriority(discoverConfig.collections).map(({ description, image, onboarding, baseFeed, ...c }: DiscoverCollection & { description?: string, image?: string, onboarding?: boolean, baseFeed?: unknown }) => c);
    setCollections(loaded);
    setOriginalCollections(JSON.parse(JSON.stringify(loaded)));

    const auth = appState.state.authData;
    if (!auth) return;
    // Batch lookups (max 30 ids per request) instead of one catalog request per feed, which trips the rate limiter.
    const allFeedIds = Array.from(new Set(loaded.flatMap(c => c.feedIds.map(f => f.feedId))));
    for (let i = 0; i < allFeedIds.length; i += 30)
      getFeedSummaries(auth, allFeedIds.slice(i, i + 30))
        .then(summaries => setFeedInfoMap(prev => {
          const next = new Map(prev);
          summaries.forEach(f => next.set(f.id, { id: f.id, title: f.shortName, domain: f.domain ?? '' }));
          return next;
        }))
        .catch(error => console.error('Error loading feed summaries:', error));
  }, [discoverConfig, appState.state.authData]);

  const hasChanges = JSON.stringify(collections) != JSON.stringify(originalCollections);

  const leave = () => {
    goTo(routerCtx, '/a/admin/feeds');
  };

  const leaveConfirmation = useConfirmationModal({
    title: 'Discard Changes?',
    children: 'You have unsaved changes. Do you really want to leave without saving?',
    confirmContent: 'Discard Changes',
    dismissContent: 'Keep Editing',
    onConfirm: leave
  });

  const handleBack = () => {
    if (hasChanges) {
      leaveConfirmation();
    } else {
      leave();
    }
  };

  useOnUnload(() => !hasChanges, hasChanges);

  const handleAddCollection = () => {
    const newCollection: DiscoverCollection = {
      id: `collection-${Date.now()}`,
      title: 'New Collection',
      feedIds: []
    };
    setCollections([...collections, newCollection]);
    setExpandedCollectionIds(new Set(expandedCollectionIds).add(newCollection.id));
  };

  const handleUpdateCollection = (index: number, updated: DiscoverCollection) => {
    const newCollections = [...collections];
    newCollections[index] = updated;
    setCollections(newCollections);
  };

  // Re-sort only once the priority field is left, so a collection doesn't jump away while its
  // priority is typed. Runs after the blur, when focus has reached its next widget, and puts focus
  // back there in case moving the collection's DOM nodes dropped it.
  const handlePriorityCommit = () => setTimeout(() => {
    const active = document.activeElement as HTMLElement | null;
    flushSync(() => setCollections(current => sortCollectionsByPriority(current)));
    if (active && document.activeElement != active)
      active.focus();
  });

  const handleDeleteCollection = (index: number) => {
    setCollections(collections.filter((_, i) => i != index));
  };

  const handleToggleCollection = (collectionId: string) => {
    const newExpanded = new Set(expandedCollectionIds);
    if (newExpanded.has(collectionId)) {
      newExpanded.delete(collectionId);
    } else {
      newExpanded.add(collectionId);
    }
    setExpandedCollectionIds(newExpanded);
  };

  const searchFeedsForCollection = useCallback(async (query: string): Promise<FeedInfo[]> => {
    const auth = nonNull(appState.state.authData);
    const results = await getAutocomplete(auth, query);
    return results.map(([id, title, domain]) => ({ id, title, domain }));
  }, [appState.state.authData]);

  // Auto-save collections when they change
  useEffect(() => {
    if (!hasChanges || loadStatus == QueryStatus.Loading || loadStatus == QueryStatus.Pending) return;

    const timeoutId = setTimeout(async () => {
      setSaving(true);
      showToast({ children: 'Saving...', type: 'info' }, 10000);
      try {
        await setStuff({ collections });
        setOriginalCollections(JSON.parse(JSON.stringify(collections)));
        showToast({ children: 'Saved', type: 'success' }, 2000);
      } catch (error) {
        console.error('Auto-save error:', error);
        showToast({ children: 'Error saving changes', type: 'error' }, 3000);
      } finally {
        setSaving(false);
      }
    }, 1000); // Auto-save after 1 second of no changes

    return () => clearTimeout(timeoutId);
  }, [collections, hasChanges, loadStatus, setStuff, showToast]);

  const loading = loadStatus == QueryStatus.Loading;

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-discover"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin adminDiscoverPage'>
              <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <AdminHeader title="Manage Collections" onExpand={onExpand} />

                <ButtonBar size="small">
                  <TextButton
                    label="Back"
                    icon={chevronBackIcon}
                    onClick={handleBack}
                    focussable="flow"
                  />
                </ButtonBar>

                <Note {...toastProps} />

                <div style={{ marginTop: '24px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0 }}>{loading ? 'Loading...' : `Collections (${collections.length})`}</h3>
                    <TextButton
                      label="Add Collection"
                      icon={addIcon}
                      onClick={handleAddCollection}
                      focussable="flow"
                      type="major"
                      size="small"
                    />
                  </div>
                  <p style={{ marginTop: 0, marginBottom: '16px', color: 'var(--gray-400)', fontSize: '0.85rem' }}>
                    There is no hard limit on feeds per collection, but aim for roughly 8–10. The full
                    feed list is delivered to every client, and each feed costs a DB lookup during
                    content generation.
                  </p>

                  {collections.length == 0 && !loading && (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)', border: '1px dashed var(--gray-850)', borderRadius: '6px' }}>
                      No collections yet. Click "Add Collection" to create one!
                    </div>
                  )}

                  {collections.map((collection, index) => (
                    <CollectionEditor
                      key={collection.id}
                      collection={collection}
                      isExpanded={expandedCollectionIds.has(collection.id)}
                      onToggle={() => handleToggleCollection(collection.id)}
                      onChange={(updated) => handleUpdateCollection(index, updated)}
                      onDelete={() => handleDeleteCollection(index)}
                      onPriorityCommit={handlePriorityCommit}
                      feedInfoMap={feedInfoMap}
                      onSearchFeed={searchFeedsForCollection}
                    />
                  ))}
                </div>
              </div>
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminDiscoverPage;
